/**
 * @fileoverview Helper functions for managing recent properties and filters
 * in local storage.
 */

const RECENT_PROPERTIES_KEY = 'ga_recent_properties';
const RECENT_FILTERS_KEY = 'ga_recent_filters';
const MAX_RECENTS = 5; // Keep a maximum of 5 recently viewed items

/**
 * Retrieves the list of recently viewed Google Analytics properties from local storage.
 * @returns {Array<Object>} An array of recently viewed property objects.
 */
export const getRecentProperties = () => {
  try {
    const stored = localStorage.getItem(RECENT_PROPERTIES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading recent properties from local storage:', error);
    return [];
  }
};

/**
 * Adds a property to the list of recently viewed properties in local storage.
 * It ensures uniqueness and limits the list size.
 * @param {Object} property - The property object to add (must have propertyId, displayName, accountDisplayName).
 */
export const addRecentProperty = (property) => {
  try {
    let recentProperties = getRecentProperties();

    // Remove if already exists to move it to the top
    recentProperties = recentProperties.filter(
      (p) => p.propertyId !== property.propertyId,
    );

    // Add the new property to the beginning
    recentProperties.unshift({
      propertyId: property.propertyId,
      displayName: property.displayName,
      accountDisplayName: property.accountDisplayName,
      // Only store essential data to keep storage light
    });

    // Limit the array size
    if (recentProperties.length > MAX_RECENTS) {
      recentProperties = recentProperties.slice(0, MAX_RECENTS);
    }

    localStorage.setItem(
      RECENT_PROPERTIES_KEY,
      JSON.stringify(recentProperties),
    );
  } catch (error) {
    console.error('Error writing recent properties to local storage:', error);
  }
};

/**
 * Retrieves the list of recently used custom filters from local storage.
 * @returns {Array<Object>} An array of recently used filter objects.
 */
export const getRecentCustomFilters = () => {
  try {
    const stored = localStorage.getItem(RECENT_FILTERS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading recent filters from local storage:', error);
    return [];
  }
};

/**
 * Adds a custom filter to the list of recently used filters in local storage.
 * It ensures uniqueness and limits the list size.
 * @param {Object} filter - The filter object to add (must have _id and name).
 */
export const addRecentCustomFilter = (filter) => {
  try {
    let recentFilters = getRecentCustomFilters();

    // Remove if already exists to move it to the top
    recentFilters = recentFilters.filter((f) => f._id !== filter._id);

    // Add the new filter to the beginning
    recentFilters.unshift({
      _id: filter._id,
      name: filter.name,
      // Store other relevant data if needed, like a summary of criteria
    });

    // Limit the array size
    if (recentFilters.length > MAX_RECENTS) {
      recentFilters = recentFilters.slice(0, MAX_RECENTS);
    }

    localStorage.setItem(RECENT_FILTERS_KEY, JSON.stringify(recentFilters));
  } catch (error) {
    console.error('Error writing recent filters to local storage:', error);
  }
};
